// Shared runtime entry screen for KURSI LEARN
import { useState, useEffect, useRef } from 'react';
import {
  View, Text, StyleSheet, Platform,
  ActivityIndicator, ScrollView, Pressable,
} from 'react-native';
import { useLocalSearchParams, router } from 'expo-router';
import Head from 'expo-router/head';
import { supabase } from '@/utils/supabase';
import { theme } from '@/constants/theme';
import { trackKursiPlayEvent } from '@/utils/kursiPlayTelemetry';
import { buildRuntimeHomePath, type RuntimeBase } from '@/features/unified-learning/runtime/runtimeRoutes';

type Stage = 'loading' | 'ready' | 'error';

const memGuestUids = new Map<string, string>();
const memGuestTokens = new Map<string, string>();

function getOrCreateGuestUid(slug: string): string {
  const cleanSlug = String(slug || '').trim();
  const key = `kursi_play_guest_uid:${cleanSlug}`;

  // Web: persist across refresh
  if (Platform.OS === 'web') {
    try {
      if (typeof window !== 'undefined' && window?.localStorage) {
        const existing = window.localStorage.getItem(key);
        if (existing) return existing;
        const created = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        window.localStorage.setItem(key, created);
        return created;
      }
    } catch {
      // Private browsing — fall through to in-memory
    }
  }

  // Native / localStorage unavailable: in-memory for session
  const existingMem = memGuestUids.get(cleanSlug);
  if (existingMem) return existingMem;
  const created = `guest_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  memGuestUids.set(cleanSlug, created);
  return created;
}

function hasStoredGuestUid(slug: string): boolean {
  const cleanSlug = String(slug || '').trim();
  const key = `kursi_play_guest_uid:${cleanSlug}`;
  if (Platform.OS === 'web') {
    try {
      if (typeof window !== 'undefined' && window?.localStorage) {
        return !!window.localStorage.getItem(key);
      }
    } catch {
      return false;
    }
  }
  return memGuestUids.has(cleanSlug);
}

export function RuntimeEntryScreen({ runtimeBase }: { runtimeBase: RuntimeBase }) {
  const { slug } = useLocalSearchParams<{ slug: string }>();

  const [course, setCourse] = useState<any>(null);
  const [stage, setStage] = useState<Stage>('loading');
  const [guestUid, setGuestUid] = useState('');
  const [guestToken, setGuestToken] = useState('');
  const [isReturningLearner, setIsReturningLearner] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [retryTick, setRetryTick] = useState(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openCourseHome = () => {
    if (!slug || !guestUid || navigating) return;
    setNavigating(true);
    trackKursiPlayEvent('play_cta_clicked', {
      slug: String(slug),
      uid_type: 'guest',
      returning: isReturningLearner,
    });
    router.replace(
      buildRuntimeHomePath(runtimeBase, String(slug), { uid: guestUid, token: guestToken }) as any,
    );
  };

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    (async () => {
      trackKursiPlayEvent('play_entry_opened', { slug: String(slug) });
      const { data, error } = await supabase
        .from('kp_worlds')
        .select('id, world_name as name, description, primary_color, status')
        .eq('slug', slug)
        .single();

      if (cancelled) return;

      if (error || !data) {
        trackKursiPlayEvent('kursi_play_error', {
          location: 'play_entry_fetch_course',
          slug: String(slug),
          message: error?.message || 'course_not_found',
        });
        setStage('error');
        return;
      }
      setCourse(data);

      const slugKey = String(slug);
      const hadStoredUid = hasStoredGuestUid(slugKey);
      const localUid = getOrCreateGuestUid(slugKey);
      setIsReturningLearner(hadStoredUid);

      let guestUid = localUid;
      let guestToken = memGuestTokens.get(slugKey) ?? '';
      try {
        const { data: sessionData } = await supabase.functions.invoke(
          'kursi-play-guest-session',
          {
            body: { slug: slugKey, guest_uid: localUid },
          },
        );
        if (sessionData?.success && sessionData?.uid && sessionData?.guest_session_token) {
          guestUid = String(sessionData.uid);
          guestToken = String(sessionData.guest_session_token);
          memGuestUids.set(slugKey, guestUid);
          memGuestTokens.set(slugKey, guestToken);
          if (Platform.OS === 'web' && typeof window !== 'undefined') {
            window.localStorage.setItem(`kursi_play_guest_uid:${slugKey}`, guestUid);
            window.localStorage.setItem(`kursi_play_guest_token:${slugKey}`, guestToken);
          }
        }
      } catch (e: any) {
        trackKursiPlayEvent('kursi_play_error', {
          location: 'play_entry_guest_session',
          slug: String(slug),
          message: e?.message || 'guest_session_failed',
        });
        if (Platform.OS === 'web' && typeof window !== 'undefined') {
          guestToken = window.localStorage.getItem(`kursi_play_guest_token:${slugKey}`) || '';
        }
      }

      if (cancelled) return;
      setGuestUid(guestUid);
      setGuestToken(guestToken);
      setStage('ready');
    })();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [slug, retryTick]);

  if (stage === 'loading') {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={theme.colors.primary} size="large" />
      </View>
    );
  }

  if (stage === 'error') {
    return (
      <View style={styles.center}>
        <Text style={styles.errorTitle}>קורס לא נמצא</Text>
        <Text style={styles.errorSub}>הלינק שגוי או שהקורס לא זמין.</Text>
        <View style={styles.errorActions}>
          <Pressable
            style={styles.secondaryBtn}
            onPress={() => {
              setStage('loading');
              setRetryTick((v) => v + 1);
            }}
            accessibilityRole="button"
            accessibilityLabel="נסה שוב"
          >
            <Text style={styles.secondaryBtnText}>נסה שוב</Text>
          </Pressable>
          <Pressable
            style={styles.ghostBtn}
            onPress={() => router.replace('/learn' as any)}
            accessibilityRole="button"
            accessibilityLabel="חזרה ל-KURSI LEARN"
          >
            <Text style={styles.ghostBtnText}>חזרה ל-KURSI LEARN</Text>
          </Pressable>
        </View>
      </View>
    );
  }



  return (
    <ScrollView style={styles.bg} contentContainerStyle={styles.container}>
      {Platform.OS === 'web' && (
        <Head>
          <meta name="robots" content="noindex, nofollow" />
        </Head>
      )}
      <View
        style={[
          styles.courseHeader,
          { borderColor: course?.primary_color || theme.colors.primary },
        ]}
      >
        <Text style={styles.courseLabel}>ברוכים הבאים ל</Text>
        <Text style={styles.courseName}>{course?.name}</Text>
        {course?.description ? <Text style={styles.courseDesc}>{course.description}</Text> : null}
      </View>

      <View style={styles.card}>
        <Text style={styles.cardTitle}>
          {isReturningLearner ? 'ברוך/ה הבא/ה חזרה' : 'מתחילים תוך רגע'}
        </Text>
        <Text style={styles.cardSub}>
          {isReturningLearner
            ? 'נמשיך בדיוק מהנקודה שהפסקת.'
            : 'לא צריך הרשמה כדי להתחיל ללמוד.'}
        </Text>
        <Pressable
          style={[styles.ctaBtn, navigating && styles.ctaBtnDisabled]}
          onPress={openCourseHome}
          disabled={navigating || !guestUid}
          accessibilityRole="button"
          accessibilityLabel={isReturningLearner ? 'ממשיכים מאיפה שהפסקת' : 'מתחילים את השיעור הראשון'}
        >
          {navigating ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.ctaText}>
              {isReturningLearner ? 'ממשיכים מאיפה שהפסקת' : 'מתחילים את השיעור הראשון'}
            </Text>
          )}
        </Pressable>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  bg: { flex: 1, backgroundColor: theme.colors.background },
  container: {
    alignItems: 'center', paddingTop: 80, paddingBottom: 60, paddingHorizontal: 20, gap: 24,
  },
  center: {
    flex: 1, alignItems: 'center', justifyContent: 'center',
    backgroundColor: theme.colors.background,
  },
  courseHeader: {
    width: '100%', maxWidth: 420, borderWidth: 1.5,
    borderRadius: theme.borderRadius.xl, padding: 24,
    alignItems: 'center', gap: 6, backgroundColor: theme.colors.surface,
  },
  courseLabel: { fontFamily: theme.fonts.regular, fontSize: 13, color: theme.colors.textMuted },
  courseName:  { fontFamily: theme.fonts.bold, fontSize: 26, color: theme.colors.text, textAlign: 'center' },
  courseDesc:  { fontFamily: theme.fonts.regular, fontSize: 14, color: theme.colors.textLight, textAlign: 'center' },
  card: {
    width: '100%', maxWidth: 420, backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.xl, borderWidth: 1, borderColor: theme.colors.border,
    padding: 24, gap: 12,
    ...(Platform.OS === 'web' ? ({ boxShadow: '0px 20px 60px rgba(0,0,0,0.20)' } as any) : {}),
  },
  cardTitle: { fontFamily: theme.fonts.bold, fontSize: 22, color: theme.colors.text, textAlign: 'right' },
  cardSub:   { fontFamily: theme.fonts.regular, fontSize: 14, color: theme.colors.textMuted, textAlign: 'right' },
  ctaBtn: {
    backgroundColor: theme.colors.primary,
    borderRadius: theme.borderRadius.md,
    minHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 16,
    marginTop: 4,
  },
  ctaBtnDisabled: { opacity: 0.7 },
  ctaText: { fontFamily: theme.fonts.bold, fontSize: 16, color: '#fff' },
  errorTitle: { fontFamily: theme.fonts.bold, fontSize: 22, color: theme.colors.text },
  errorSub:   { fontFamily: theme.fonts.regular, fontSize: 14, color: theme.colors.textMuted, marginTop: 4 },
  errorActions: { marginTop: 14, width: '100%', maxWidth: 320, gap: 10 },
  secondaryBtn: {
    minHeight: 44,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.surface,
  },
  secondaryBtnText: { fontFamily: theme.fonts.bold, fontSize: 15, color: theme.colors.primary },
  ghostBtn: {
    minHeight: 44,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.border,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.surface,
  },
  ghostBtnText: { fontFamily: theme.fonts.regular, fontSize: 14, color: theme.colors.textMuted },
  comingSoonCard: {
    width: '100%', maxWidth: 420, backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.xl, borderWidth: 1.5,
    borderColor: theme.colors.border, padding: 28, gap: 14, alignItems: 'center',
  },
  comingSoonName: {
    fontFamily: theme.fonts.bold, fontSize: 24, color: theme.colors.text, textAlign: 'center',
  },
  comingSoonHeading: {
    fontFamily: theme.fonts.bold, fontSize: 20, textAlign: 'center', marginTop: 4,
  },
  comingSoonSub: {
    fontFamily: theme.fonts.regular, fontSize: 15, color: theme.colors.textMuted,
    textAlign: 'center',
  },
  comingSoonStatusRow: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 8, marginTop: 8,
  },
  comingSoonStatusText: {
    fontFamily: theme.fonts.regular, fontSize: 14, color: theme.colors.textLight,
    textAlign: 'right',
  },
});
